// The top of the screen: the board pill in the middle (switch boards, fit,
// theme, share, settings) and, on the right, your agents and your account.

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { isBoardChat } from "../../shared/authors";
import { Archive, ArrowDown, ArrowUp, Check, Maximize2, MoreHorizontal, RotateCcw, ChevronDown, ChevronRight, CircleHelp, CloudOff, CreditCard, Folder, FolderInput, FolderMinus, FolderPlus, Globe, House, Keyboard, LayoutGrid, LogOut, Mail, Pencil, Plug, Plus, ScrollText, Settings2, Trash2, UserRound, Users } from "lucide-react";
import { cx, initials, isMac } from "../lib/util";
import { DEMO, START_URL } from "../demo/mode";
import { addFolder, archiveBoard, archivedOn, deleteBoard, deleteFolder, dropBoard, homeBoard, moveLineBy, placeAboveFolder, placeFolder, renameFolder, restoreBoard, setBoardFolder, setHome, switchBoard, updateBoardById } from "../state/actions";
import { openContextMenu } from "./ContextMenu";
import { TAB_KINDS, enterFullscreen } from "./Fullscreen";
import { KIND_ICON } from "../boards/kinds";
import { Bell, BoardCount, FolderCount } from "./Bell";
import type { Board, BoardFolder } from "../../shared/types";
import { keyedBoards, topLevel } from "../../shared/boardsOrder";
import { kindOf } from "../../shared/boardKinds";
import { boardItems, currentBoard, get, set, useStore } from "../state/store";
import { readShared, sharedHref } from "../state/guest";
import { signOut } from "../state/sync";
import { toggleChat, useChat } from "../agent/store";
import { askLabel, useBuilder } from "../agent/handoff";
import { camera } from "../canvas/camera";
import { bounds, itemRect } from "../canvas/geometry";
import { withMap } from "../boards/mindmap/live";
import { IconButton } from "./ui";
import { HelpButton } from "../help/HelpButton";
import { PlanWaiting } from "../boards/plan/waiting";
import { Mark } from "./Logo";
import { openOverview } from "./Panels";
import { useVoicePrefs } from "../voice/prefs";
import { BoardActions, TabsLook } from "./menus/BoardActions";
import { BoardTabs } from "./BoardTabs";
import { useTabLook, useTabsOn } from "./useTabs";
import { PillRename, renameBoard, useRenaming } from "../boards/Rename";
import { folderIsOpen, setFolderOpen, useFoldersOpen, useKeyPrefs } from "../state/boardKeys";
import { readStored, writeStored } from "../lib/stored";

const MicButton = lazy(() => import("../voice/MicButton"));

export function SaveDot() {
  const save = useStore((s) => s.save);
  const err = useStore((s) => s.saveError);
  const live = useStore((s) => s.live);
  // calm by default (Features 4, Tidy): nothing while every change is saved and the board is live. Offline or a
  // failure shows at once; a save or a reconnect that takes more than a moment shows once it has.
  const settling = save === "saving" || (save === "saved" && live !== "live");
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (!settling) return setLate(false);
    const t = setTimeout(() => setLate(true), 1500);
    return () => clearTimeout(t);
  }, [settling]);
  if (save === "saved" && live === "live") return null;
  if (settling && !late) return null;
  const label =
    save === "saving" ? "Saving" : save === "offline" ? "Offline. Your changes are kept and will save when you are back." : save === "error" ? `Not saved: ${err ?? "something went wrong"}` : live === "live" ? "Live" : live === "offline" ? "Saved. Reconnecting for live updates." : "Saved";
  return (
    <span className={cx("save-dot", `save-${save}`, live !== "live" && save === "saved" && "is-quiet")} data-tip={label} aria-label={label}>
      {save === "offline" ? <CloudOff size={12} /> : <i />}
    </span>
  );
}

/** Emoji a board is often named by, a click away; any other can be typed or pasted. */
const EMOJI = ["🚀", "📈", "📊", "💼", "💰", "🎯", "🧠", "💡", "🛠️", "🗂️", "📅", "✅", "📝", "📣", "🤝", "🏆", "🌱", "🔥", "⚡", "⭐", "🎨", "🎵", "📚", "🎓", "💻", "📱", "🛰️", "🧩", "🎮", "🧪", "🔬", "🌍", "🗺️", "✈️", "🏠", "🏃", "🍳", "🛒", "📦", "🐶", "☀️", "🌙", "🌊", "🏔️", "❤️", "🧾"];

/** One emoji, or none, for a board: picked from the grid, typed in the box, or taken away. */
export function EmojiPicker({ name, value, onPick, onClose }: { name: string; value: string | null; onPick: (e: string | null) => void; onClose: () => void }) {
  const [typed, setTyped] = useState("");
  // the first character a person sees, so a pasted word or flag keeps only its first emoji
  const first = (t: string) => [...new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(t.trim())][0]?.segment ?? "";
  return (
    <div className="emoji-pick" onPointerDown={(e) => e.stopPropagation()}>
      <div className="emoji-pick-head">An emoji for “{name}”</div>
      <div className="emoji-grid">
        {EMOJI.map((e) => (
          <button key={e} className={cx(e === value && "is-on")} aria-label={`Use ${e}`} onClick={() => onPick(e)}>
            {e}
          </button>
        ))}
      </div>
      <div className="emoji-foot">
        <input
          value={typed}
          placeholder="Or type one"
          aria-label="Type or paste an emoji"
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter" && first(typed)) onPick(first(typed));
            if (e.key === "Escape") onClose();
          }}
        />
        <button className="btn btn-xs btn-ghost" onClick={() => onPick(null)} disabled={!value}>
          No emoji
        </button>
      </div>
    </div>
  );
}

const NO_FOLDERS: BoardFolder[] = [];
/** Archived, at the foot of the list: folded shut until opened, and left as it was, in this browser */
const SHELF_OPEN = "cb:archived:open";

function BoardMenu({ onClose }: { onClose: () => void }) {
  const boards = useStore((s) => s.boards);
  const boardId = useStore((s) => s.boardId);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [picking, setPicking] = useState<string | null>(null);
  // dragging a board to a new place: which one, and where it would land
  const [drag, setDrag] = useState<{ id: string; over: number } | null>(null);
  // folders (David, 16 Oct): the folder a board is dragged onto, the one being renamed, the board choosing one
  const list = useStore((s) => s.workspace?.folders ?? NO_FOLDERS);
  const home = useStore((s) => homeBoard(s)?.id ?? null);
  const [dropFolder, setDropFolder] = useState<string | null>(null);
  // a folder dragged to a new place, and a board held just above a folder header, to stand above it (Round 6)
  const [dragFolder, setDragFolder] = useState<string | null>(null);
  const [dropAbove, setDropAbove] = useState<string | null>(null);
  const endDrag = () => (setDrag(null), setDragFolder(null), setDropFolder(null), setDropAbove(null));
  const [naming, setNaming] = useState<string | null>(null);
  const [filing, setFiling] = useState<string | null>(null);
  // a board's other actions, behind its one ... button (Fixes 10: a row of hover buttons got in the way of the click)
  const [more, setMore] = useState<string | null>(null);
  // which folders are folded, kept in this browser; the number keys skip a folded folder's boards, as the list does
  const opened = useFoldersOpen((s) => s.opened);
  const numbers = useKeyPrefs((s) => s.numbers);
  const numberOf = new Map(numbers ? keyedBoards(list, boards, (id) => folderIsOpen(opened, id)).map((b, i) => [b.id, i + 1]) : []);
  // a board opened by its number while the list is open: the list closes, as a click on the board would close it
  const openedOn = useRef(boardId);
  useEffect(() => {
    if (boardId !== openedOn.current) onClose();
  }, [boardId, onClose]);
  const [query, setQuery] = useState("");
  const sharedList = useStore((s) => s.shared);
  // looked at again each time the list opens, so a board shared a moment ago is there
  useEffect(() => {
    void readShared()
      .then((shared) => set({ shared }))
      .catch(() => null);
  }, []);
  // archived boards (Features 1, 20 Oct): out of the list, at its foot, folded shut with their count
  const shelf = useStore((s) => s.archived);
  const [shelfOpen, setShelfOpenState] = useState(() => readStored(SHELF_OPEN) === "1");
  const setShelfOpen = (open: boolean) => {
    setShelfOpenState(open);
    writeStored(SHELF_OPEN, open ? "1" : "0"); // kept for this visit only when storage is out of reach
  };
  const searching = boards.length > 6 || list.length > 0 || shelf.length > 0;
  const found = query.trim() ? boards.filter((b) => b.name.toLowerCase().includes(query.trim().toLowerCase())) : null;
  const foundShelved = query.trim() ? shelf.filter((b) => b.name.toLowerCase().includes(query.trim().toLowerCase())) : [];
  /** A right-click on a board: its own menu (Open, Archive or Restore, Delete). */
  const boardMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    openContextMenu({ x: e.clientX, y: e.clientY, kind: "board", ids: [id], at: null });
  };
  /** An archived board's row: opens it read only; Restore and Delete beside it. */
  const shelvedRow = (b: Board) => (
    <div key={b.id} className="board-row-wrap">
      <div className={cx("board-row is-archived", b.id === boardId && "is-current")} onContextMenu={(e) => boardMenu(e, b.id)}>
        <span className="board-emoji-btn" aria-hidden>
          {b.emoji ? <span className="board-emoji">{b.emoji}</span> : <KindMark board={b} />}
        </span>
        <button
          className="board-row-main"
          onClick={() => {
            switchBoard(b.id);
            onClose();
          }}
        >
          <span className="board-row-name">{b.name}</span>
          <span className="board-archived-on">{archivedOn(b.archived!)}</span>
          {b.id === boardId && <Check size={13} className="board-check" />}
        </button>
        <span className="board-row-tools">
          <button aria-label={`Restore ${b.name}`} data-tip="Restore" onClick={() => restoreBoard(b.id)}>
            <RotateCcw size={12} />
          </button>
          <button aria-label={`Delete ${b.name}`} data-tip="Delete" onClick={() => confirm(`Delete "${b.name}"? It stays in the trash for 30 days.`) && deleteBoard(b.id)}>
            <Trash2 size={12} />
          </button>
        </span>
      </div>
    </div>
  );
  const known = new Set(list.map((fo) => fo.id));
  // folders and the boards in no folder in one order, so a board can stand above the folders (Round 6)
  const lines = topLevel(list, boards);
  const lineAt = (ref: { board?: string; folder?: string }) => lines.findIndex((e) => ("folder" in e ? e.folder.id === ref.folder : e.board.id === ref.board));
  /** One board's row, among the boards it moves up and down with (group); in a folder, set in under it. */
  const row = (b: Board, group: Board[], inFolder = false) => {
    const i = boards.indexOf(b);
    const k = group.indexOf(b);
    // its number key, quietly at the row's end; not in a search's results, where a number typed goes in the box
    const n = found ? undefined : numberOf.get(b.id);
    return (
      <div key={b.id} className="board-row-wrap">
        <div
          key={b.id}
          className={cx("board-row", inFolder && "in-folder", b.id === boardId && "is-current", drag?.id === b.id && "is-dragging", drag && drag.id !== b.id && drag.over === i && "is-drop", dragFolder && dropFolder === b.id && "is-drop-line")}
          draggable={renaming !== b.id}
          onContextMenu={(e) => renaming !== b.id && boardMenu(e, b.id)}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", b.name);
            setDrag({ id: b.id, over: i });
          }}
          onDragOver={(e) => {
            if (dragFolder) {
              // a folder held over a board: it lands at that board's place in the list (its folder's, in one)
              e.preventDefault();
              if (dropFolder !== b.id) setDropFolder(b.id);
              return;
            }
            if (!drag) return;
            e.preventDefault();
            if (drag.over !== i) setDrag({ ...drag, over: i });
          }}
          onDrop={(e) => {
            e.preventDefault();
            // a board dropped on another takes its place, and its folder; a folder dropped on one takes its line
            if (dragFolder) placeFolder(dragFolder, inFolder && b.folder ? { folder: b.folder } : { board: b.id });
            else if (drag) dropBoard(drag.id, b.id);
            endDrag();
          }}
          onDragEnd={endDrag}
        >
          <button className="board-emoji-btn" aria-label={b.emoji ? `Change the emoji ${b.emoji}` : "Add an emoji"} title={b.emoji ? "Change or remove the emoji" : "Add an emoji"} onClick={() => setPicking(picking === b.id ? null : b.id)}>
            {b.emoji ? <span className="board-emoji">{b.emoji}</span> : <KindMark board={b} />}
          </button>
          {renaming === b.id ? (
            <input
              autoFocus
              defaultValue={b.name}
              className="board-rename"
              onFocus={(e) => e.currentTarget.select()}
              onBlur={(e) => {
                const v = e.currentTarget.value.trim();
                setRenaming(null);
                if (v && v !== b.name) updateBoardById(b.id, { name: v.slice(0, 80) }, "Rename board");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  e.currentTarget.value = b.name;
                  e.currentTarget.blur();
                }
                e.stopPropagation();
              }}
            />
          ) : (
            <button
              className="board-row-main"
              aria-current={b.id === boardId ? "page" : undefined}
              aria-keyshortcuts={n ? String(n) : undefined}
              onClick={() => {
                // a click opens the board, the one in view too (Fixes 10); Rename is in its ... menu, or a double click
                if (b.id !== boardId) switchBoard(b.id);
                onClose();
              }}
              onDoubleClick={() => setRenaming(b.id)}
            >
              <span className="board-row-name">{b.name}</span>
              {b.id === home &&<House size={12} className="board-home" aria-label="Home board" />}
              {b.share.enabled && <span className="board-public">Public</span>}
              {b.id === boardId && <Check size={13} className="board-check" />}
            </button>
          )}
          {renaming !== b.id && <BoardCount boardId={b.id} dismissable="button" />}
          {n && renaming !== b.id && (
            <kbd className="board-key" aria-hidden>
              {n}
            </kbd>
          )}
          {renaming !== b.id && (
            <button className={cx("board-row-more", more === b.id && "is-open")} aria-label={`More for ${b.name}`} aria-haspopup="menu" aria-expanded={more === b.id} onClick={() => setMore(more === b.id ? null : b.id)}>
              <MoreHorizontal size={14} />
            </button>
          )}
        </div>
        {more === b.id && (
          <div className="board-file board-more" role="menu" aria-label={`More for ${b.name}`} onClick={() => setMore(null)}>
            <button role="menuitem" onClick={() => setRenaming(b.id)}>
              <Pencil size={12} /> Rename
            </button>
            {/* in a folder, among its boards; in none, up and down the list, past a whole folder at a time (Round 6).
                In a search's results, where neither shows, they are off */}
            <button role="menuitem" disabled={found ? true : inFolder ? !group[k - 1] : lineAt({ board: b.id }) <= 0} onClick={() => (inFolder ? group[k - 1] && dropBoard(b.id, group[k - 1].id) : moveLineBy({ board: b.id }, -1))}>
              <ArrowUp size={12} /> Move up
            </button>
            <button role="menuitem" disabled={found ? true : inFolder ? !group[k + 1] : lineAt({ board: b.id }) < 0 || lineAt({ board: b.id }) >= lines.length - 1} onClick={() => (inFolder ? group[k + 1] && dropBoard(b.id, group[k + 1].id) : moveLineBy({ board: b.id }, 1))}>
              <ArrowDown size={12} /> Move down
            </button>
            <button role="menuitem" onClick={() => setHome(b.id === home ? null : b.id)}>
              <House size={12} /> {b.id === home ? "Stop using as home" : "Make home board"}
            </button>
            <button role="menuitem" onClick={() => setFiling(b.id)}>
              <FolderInput size={12} /> Move to a folder
            </button>
            <button role="menuitem" disabled={boards.length <= 1} onClick={() => archiveBoard(b.id)}>
              <Archive size={12} /> Archive
            </button>
            <button role="menuitem" className="board-more-delete" disabled={boards.length <= 1} onClick={() => confirm(`Delete "${b.name}"? It stays in the trash for 30 days.`) && deleteBoard(b.id)}>
              <Trash2 size={12} /> Delete
            </button>
          </div>
        )}
        {filing === b.id && (
          <div className="board-file" role="menu" aria-label={`Move ${b.name} to a folder`}>
            {list.map((fo) => (
              <button key={fo.id} role="menuitemradio" aria-checked={b.folder === fo.id} className={cx(b.folder === fo.id && "is-on")} onClick={() => (setBoardFolder(b.id, fo.id), setFiling(null))}>
                <Folder size={12} /> {fo.name}
              </button>
            ))}
            {b.folder && known.has(b.folder) && (
              <button role="menuitem" onClick={() => (setBoardFolder(b.id, null), setFiling(null))}>
                <FolderMinus size={12} /> No folder
              </button>
            )}
            <button
              role="menuitem"
              onClick={() => {
                const id = addFolder();
                setBoardFolder(b.id, id);
                setFiling(null);
                setNaming(id);
              }}
            >
              <FolderPlus size={12} /> New folder
            </button>
          </div>
        )}
      </div>
    );
  };
  useEffect(() => {
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest(".board-pill-name") && onClose();
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", key);
    };
  }, [onClose]);
  // a board just made is the one open, and its row, the last, sits below the list's fold: bring the open one into view (Fixes 10)
  useEffect(() => {
    const list = listRef.current;
    const at = list?.querySelector<HTMLElement>(".board-row.is-current");
    if (!list || !at) return;
    const l = list.getBoundingClientRect();
    const r = at.getBoundingClientRect();
    if (r.top < l.top) list.scrollTop += r.top - l.top;
    else if (r.bottom > l.bottom) list.scrollTop += r.bottom - l.bottom;
  }, [boardId, boards.length]);
  return (
    <div ref={ref} className={cx("board-menu island", numberOf.size > 0 && !found && "has-keys")}>
      {searching && (
        <input
          className="board-search"
          value={query}
          placeholder="Find a board"
          aria-label="Find a board"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            e.stopPropagation();
            // Enter opens the first that fits, as a click on it would
            if (e.key === "Enter" && found?.[0]) {
              switchBoard(found[0].id);
              onClose();
            }
            if (e.key === "Escape") setQuery("");
          }}
        />
      )}
      <div ref={listRef} className="board-menu-list" onDragOver={(e) => drag && e.preventDefault()}>
        {found ? (
          // a search finds every board, whatever folder it is in, and archived ones after them
          found.length || foundShelved.length ? (
            <>
              {found.map((b) => row(b, found))}
              {foundShelved.length > 0 && <p className="board-shelf-label">Archived</p>}
              {foundShelved.map(shelvedRow)}
            </>
          ) : (
            <p className="board-none">No board called that.</p>
          )
        ) : (
          <>
            {lines.map((line, at) => {
              if ("board" in line) return row(line.board, [], false);
              const fo = line.folder;
              const inside = line.boards;
              const isOpen = folderIsOpen(opened, fo.id);
              return (
                <div key={fo.id} className={cx("board-folder-group", dragFolder === fo.id && "is-dragging")}>
                  <div
                    className={cx("board-folder", drag && dropFolder === fo.id && !dropAbove && "is-drop", ((drag && dropAbove === fo.id) || (dragFolder && dragFolder !== fo.id && dropFolder === fo.id)) && "is-drop-line")}
                    draggable={naming !== fo.id}
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", fo.name);
                      setDragFolder(fo.id);
                    }}
                    onDragEnd={endDrag}
                    onDragOver={(e) => {
                      if (dragFolder) {
                        e.preventDefault();
                        if (dropFolder !== fo.id) setDropFolder(fo.id);
                        return;
                      }
                      if (!drag) return;
                      e.preventDefault();
                      // the header's top third: the board stands above the folder, in none; the rest: into it
                      const r = e.currentTarget.getBoundingClientRect();
                      const above = e.clientY < r.top + r.height / 3 ? fo.id : null;
                      if (dropFolder !== fo.id) setDropFolder(fo.id);
                      if (dropAbove !== above) setDropAbove(above);
                    }}
                    onDragLeave={() => (setDropFolder(null), setDropAbove(null))}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragFolder) placeFolder(dragFolder, { folder: fo.id });
                      else if (drag && dropAbove === fo.id) placeAboveFolder(drag.id, fo.id);
                      else if (drag) setBoardFolder(drag.id, fo.id);
                      endDrag();
                    }}
                  >
                    {naming === fo.id ? (
                      <input
                        autoFocus
                        defaultValue={fo.name}
                        className="board-rename"
                        aria-label="Folder name"
                        onFocus={(e) => e.currentTarget.select()}
                        onBlur={(e) => {
                          setNaming(null);
                          if (e.currentTarget.value.trim() && e.currentTarget.value.trim() !== fo.name) renameFolder(fo.id, e.currentTarget.value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                          if (e.key === "Escape") {
                            e.currentTarget.value = fo.name;
                            e.currentTarget.blur();
                          }
                          e.stopPropagation();
                        }}
                      />
                    ) : (
                      <button className="board-folder-main" aria-expanded={isOpen} onClick={() => setFolderOpen(fo.id, !isOpen)} onDoubleClick={() => setNaming(fo.id)}>
                        <ChevronRight size={13} className={cx("board-folder-chev", isOpen && "is-open")} />
                        <span className="board-row-name">{fo.name}</span>
                        {/* a count in words, never a bare number beside the boards' number keys (Fixes 10) */}
                        <span className="board-folder-count">{inside.length === 1 ? "1 board" : `${inside.length} boards`}</span>
                        <FolderCount folderId={fo.id} dismissable />
                      </button>
                    )}
                    <span className="board-row-tools">
                      <button aria-label={`Rename the folder ${fo.name}`} onClick={() => setNaming(fo.id)}>
                        <Pencil size={12} />
                      </button>
                      <button aria-label={`Move the folder ${fo.name} up`} disabled={at <= 0} onClick={() => moveLineBy({ folder: fo.id }, -1)}>
                        <ArrowUp size={12} />
                      </button>
                      <button aria-label={`Move the folder ${fo.name} down`} disabled={at >= lines.length - 1} onClick={() => moveLineBy({ folder: fo.id }, 1)}>
                        <ArrowDown size={12} />
                      </button>
                      <button aria-label={`Delete the folder ${fo.name}`} onClick={() => confirm(`Delete the folder "${fo.name}"? Its boards stay, back in the list.`) && deleteFolder(fo.id)}>
                        <Trash2 size={12} />
                      </button>
                    </span>
                  </div>
                  {isOpen && inside.map((b) => row(b, inside, true))}
                  {isOpen && !inside.length && <p className="board-folder-empty">Drag a board here</p>}
                </div>
              );
            })}
            {/* boards others shared with this person (Features 1, sharing): each opens on this page with its pass */}
            {sharedList.length > 0 && (
              <div className="board-shelf board-shared">
                <p className="board-shelf-label">
                  <Users size={12} aria-hidden /> Shared with you
                </p>
                {sharedList.map((b) => (
                  <div key={`${b.ws}.${b.board}`} className="board-row-wrap">
                    <div className="board-row is-shared">
                      <span className="board-emoji-btn" aria-hidden>
                        {b.emoji ? <span className="board-emoji">{b.emoji}</span> : <KindMark board={b} />}
                      </span>
                      <a className="board-row-main" href={sharedHref(b)}>
                        <span className="board-row-name">{b.name || "A board"}</span>
                        <span className="board-shared-by">
                          {b.owner ?? ""} · {b.role === "edit" ? "can edit" : "can view"}
                        </span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {shelf.length > 0 && (
              <div className="board-shelf">
                <div className="board-folder">
                  <button className="board-folder-main" aria-expanded={shelfOpen} onClick={() => setShelfOpen(!shelfOpen)}>
                    <ChevronRight size={13} className={cx("board-folder-chev", shelfOpen && "is-open")} />
                    <Archive size={12} className="board-shelf-ico" aria-hidden />
                    <span className="board-row-name">Archived</span>
                    <span className="board-folder-count">· {shelf.length}</span>
                  </button>
                </div>
                {shelfOpen && shelf.map(shelvedRow)}
              </div>
            )}
          </>
        )}
      </div>
      {/* under the list, never inside it: the list scrolls, and would cut it off */}
      {(() => {
        const pb = boards.find((x) => x.id === picking);
        return pb ? (
          <EmojiPicker
            name={pb.name}
            value={pb.emoji}
            onClose={() => setPicking(null)}
            onPick={(e) => {
              updateBoardById(pb.id, { emoji: e }, e ? "Board emoji" : "Remove board emoji");
              setPicking(null);
            }}
          />
        ) : null;
      })()}
      <div className="menu-sep" />
      {/* one dialog for every way to start a board: its kind, or a canvas from a template */}
      <button
        className="menu-item"
        onClick={() => {
          onClose();
          set({ panel: "newboard", panelArg: null });
        }}
      >
        <span className="menu-ico">
          <Plus size={14} />
        </span>
        <span className="menu-label">New board</span>
      </button>
      <button className="menu-item" onClick={() => setNaming(addFolder())}>
        <span className="menu-ico">
          <FolderPlus size={14} />
        </span>
        <span className="menu-label">New folder</span>
      </button>
    </div>
  );
}

/** A board with no emoji shows its kind's icon, never an empty circle (Fixes 10). */
function KindMark({ board }: { board: { kind?: string } }) {
  const Icon = KIND_ICON[kindOf(board as Pick<Board, "kind">)] ?? KIND_ICON.canvas;
  return <Icon size={14} className="board-kind-mark" aria-hidden />;
}

/**
 * The board's name, which opens the boards list (and is renamed by a double-click): in the top bar's pill, and in
 * full screen's bar, the same one (Features 7). The list itself is drawn beside it by whoever holds the open state.
 */
export function BoardName({ board, menu, setMenu }: { board: Board; menu: boolean; setMenu: (open: boolean) => void }) {
  // its name on its title, as a field (Fixes 9): a double-click on it, Rename in the board menu or the command bar
  const renaming = useRenaming((r) => r.inline && r.id === board.id);
  if (renaming)
    return (
      <span className="board-pill-name is-renaming">
        {board.emoji && <span className="board-emoji">{board.emoji}</span>}
        <PillRename board={board} />
      </span>
    );
  return (
    <button
      className={cx("board-pill-name", menu && "is-open")}
      onClick={(e) => {
        // the second click of a double-click renames; it never opens and shuts the list again
        if (e.detail > 1) return;
        setMenu(!menu);
      }}
      onDoubleClick={() => {
        if (board.archived) return;
        setMenu(false);
        renameBoard(board.id);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        setMenu(false);
        openContextMenu({ x: e.clientX, y: e.clientY, kind: "board", ids: [board.id], at: null });
      }}
      aria-haspopup="menu"
      aria-expanded={menu}
    >
      {board.emoji && <span className="board-emoji">{board.emoji}</span>}
      <span>{board.name}</span>
      {kindOf(board) === "plan" && <PlanWaiting boardId={board.id} />}
      <ChevronDown size={13} />
    </button>
  );
}

/** The boards list under the name, for a bar that holds the name. */
export { BoardMenu };

export function TopBar() {
  const board = useStore(currentBoard);
  const phone = useStore((s) => s.mobile);
  const [menu, setMenu] = useState(false);
  // the boards as tabs instead of the pill, when the account has them on (Features 7)
  const tabs = useTabsOn();
  if (!board) return null;
  if (tabs && !phone) return <BoardTabs board={board} />;
  return (
    <div className="topbar">
      <div className="board-pill island">
        <BoardName board={board} menu={menu} setMenu={setMenu} />
        <InstructionsMark board={board} />
        <SharedMark board={board} />
        <span className="pill-sep" />
        {/* every board, of every kind, in one click from the same place (David, 15 Oct) */}
        <IconButton label="All boards" kbd="G" onClick={() => openOverview()}>
          <LayoutGrid size={15} />
        </IconButton>
        {/* tabs or the pill, and the tabs' icons, always in view (David, 1 Nov); a phone keeps its own bar */}
        {!phone && <TabsLook />}
        {/* a doc, sheet, deck or files board filling the tab, in one click (Fixes 10); a phone's screen is the board already */}
        {TAB_KINDS.has(kindOf(board)) && !phone && (
          <IconButton label="Full screen" kbd="F" onClick={() => void enterFullscreen()}>
            <Maximize2 size={15} />
          </IconButton>
        )}
        {/* what is done to this board and used less than daily (Features 4, Tidy): Share, Look and themes, its
            instructions, See everything, Full screen and the rest, in the board menu */}
        <BoardActions board={board} />
        <SaveDot />
      </div>
      {/* beside the pill, not in it: the pill's glass blurs, and a menu's blur inside it would blur nothing (QA, 26 Oct) */}
      {menu && <BoardMenu onClose={() => setMenu(false)} />}
    </div>
  );
}

/**
 * A small quiet mark beside the board's name while it has instructions for agents (Features 3), which opens them. No
 * dot (Features 4): an agent's change is saved at once and shows in Board activity. A draft left from before then
 * keeps the mark, so it can be opened, used or dropped. Nothing when it has neither.
 */
export function InstructionsMark({ board }: { board: Board }) {
  if (!board.instructions && !board.instructionsDraft) return null;
  const label = "Instructions for agents";
  return (
    <button className="instr-mark" aria-label={label} data-tip={label} onClick={() => set({ panel: "instructions", panelArg: null })}>
      <ScrollText size={13} />
    </button>
  );
}

/** A small quiet mark beside the board's name while its public link is on (Features 4, Tidy), which opens Share. */
export function SharedMark({ board }: { board: Board }) {
  if (!board.share.enabled || board.archived) return null;
  return (
    <button className="instr-mark shared-mark" aria-label="Shared publicly" data-tip="Shared publicly" onClick={() => set({ panel: "share" })}>
      <Globe size={13} />
    </button>
  );
}

/** Top left: Home, the Context Board mark, back to the board the user made home from anywhere; with none yet, what it is and how to set it. */
export function TopLeft() {
  const home = useStore((s) => homeBoard(s));
  const board = useStore(currentBoard);
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setMenu(false);
    const key = (e: KeyboardEvent) => e.key === "Escape" && setMenu(false);
    window.addEventListener("pointerdown", close, true);
    window.addEventListener("keydown", key);
    return () => {
      window.removeEventListener("pointerdown", close, true);
      window.removeEventListener("keydown", key);
    };
  }, [menu]);
  if (!board) return null;
  const here = home?.id === board.id;
  const label = home ? (here ? `Home: ${home.name}` : `Go home to ${home.name}`) : "Home";
  // on the home board it says nothing (David, 17 Oct): a click shows the whole board, as a logo on a site's home page
  // goes back to its top; with no home yet, what Home is and how to set it
  const click = () => {
    if (home && !here) return switchBoard(home.id);
    if (!home) return setMenu(!menu);
    if (kindOf(board) === "canvas" || kindOf(board) === "mindmap") camera.fit(bounds(withMap(boardItems(get()).map(itemRect))), { maxZ: 1 });
  };
  return (
    <div className="topleft" ref={ref}>
      <button className={cx("home-btn", here && "is-home", menu && "is-open")} aria-label={label} data-tip={menu ? undefined : label} aria-expanded={home ? undefined : menu} onClick={click}>
        {/* the mark is the way home, as an app's logo is, bare on the scene (David, 17 Oct) */}
        <Mark size={28} />
      </button>
      {menu && !home && (
        <div className="menu island home-menu">
          <p className="home-menu-text">Home brings you back to one board. Change it any time in the boards list.</p>
          <button className="menu-item is-accent" onClick={() => (setHome(board.id), setMenu(false))}>
            <span className="menu-ico">
              <House size={14} />
            </span>
            <span className="menu-label">Make “{board.name}” home</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function TopRight() {
  const agents = useStore((s) => s.agents);
  const keys = useStore((s) => s.keys);
  const ws = useStore((s) => s.workspace);
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setMenu(false);
    window.addEventListener("pointerdown", close, true);
    return () => window.removeEventListener("pointerdown", close, true);
  }, [menu]);
  const connected = keys.filter((k) => k.kind === "agent" && k.lastUsedAt);
  const names = [...new Set([...Object.keys(agents), ...connected.map((k) => k.label)])].filter((n) => !isBoardChat(n)).slice(0, 4);
  const chatOpen = useChat((s) => s.open);
  const builder = useBuilder();
  const voiceOn = useVoicePrefs((s) => s.on);
  // with the boards as tabs, the corner is one island: the bell, the chat and you, with Connect and Help in your menu and
  // a dot on your initial while an agent is connected (David, 1 Nov: the corner looked cluttered beside the tabs)
  const tabsOn = useTabsOn();
  const phone = useStore((s) => s.mobile);
  const bar = useTabLook().bar;
  const one = tabsOn && !phone;
  const helpOn = useStore((s) => s.panel === "help");
  const item = (icon: React.ReactNode, label: string, run: () => void, note?: React.ReactNode) => (
    <button
      className="menu-item"
      onClick={() => {
        setMenu(false);
        run();
      }}
    >
      <span className="menu-ico">{icon}</span>
      <span className="menu-label">{label}</span>
      {note}
    </button>
  );
  return (
    <div className={cx("topright", one && "is-one", one && !bar && "island")}>
      {/* talk to the board, once voice is on on this computer */}
      {voiceOn && (
        <Suspense fallback={null}>
          <MicButton />
        </Suspense>
      )}
      {/* what the board raised for the user: reminders, what came due, an agent that needs them (Features 1, 20 Oct) */}
      <Bell />
      {/* the chat, by the name of whoever answers in it: a slim pill, words only */}
      <button className={cx("chat-btn island", chatOpen && "is-on")} onClick={toggleChat} aria-pressed={chatOpen} data-tip={`${chatOpen ? "Close the chat" : "Open the chat"}  ${isMac ? "⌘J" : "Ctrl J"}`}>
        <span>{askLabel(builder)}</span>
      </button>
      {/* Connect is the plug beside the account, and says what it is when pointed at */}
      {!one && (
        <button className={cx("connect-btn island", names.length > 0 && "is-linked")} onClick={() => set({ panel: "connect" })} aria-label={names.length ? `Connect (${names.join(", ")} connected)` : "Connect"} data-tip="Connect">
          <Plug size={15} />
        </button>
      )}
      {/* help for the board in view (Features 4, onboarding): the account's left neighbour */}
      {!one && <HelpButton />}
      <div className="account" ref={ref}>
        <button className="account-btn island" onClick={() => setMenu(!menu)} aria-label={one && names.length ? `Account (${names.join(", ")} connected)` : "Account"} aria-expanded={menu} data-tip={menu ? undefined : `Account and Settings  ${isMac ? "⌘," : "Ctrl+,"}`}>
          {ws?.email ? initials(ws.email) : <UserRound size={17} />}
          {one && names.length > 0 && <i className="account-dot" aria-hidden />}
        </button>
        {menu && (
          <div className="menu island account-menu">
            <div className="account-head">
              <b>{ws?.name}</b>
              <span>{DEMO ? "A demo. Nothing you change is saved." : (ws?.email ?? "Saved on this device only")}</span>
            </div>
            {one && (
              <>
                {item(<Plug size={14} />, "Connect", () => set({ panel: "connect" }), <span className={cx("menu-kbd", names.length > 0 && "is-linked")}>{names.length ? `${names.length} connected` : "None yet"}</span>)}
                {item(<CircleHelp size={14} />, "Help", () => set({ panel: helpOn ? null : "help", panelArg: null }))}
                <div className="menu-sep" />
              </>
            )}
            {!ws?.email && (
              <button
                className="menu-item is-accent"
                onClick={() => {
                  setMenu(false);
                  set({ panel: "settings", panelArg: { tab: "account" } });
                }}
              >
                <span className="menu-ico">
                  <Mail size={14} />
                </span>
                <span className="menu-label">Save it with your email</span>
              </button>
            )}
            <button
              className="menu-item"
              onClick={() => {
                setMenu(false);
                set({ panel: "settings" });
              }}
            >
              <span className="menu-ico">
                <Settings2 size={14} />
              </span>
              <span className="menu-label">Settings</span>
              <span className="menu-kbd">{isMac ? "⌘," : "Ctrl+,"}</span>
            </button>
            {ws?.email && !DEMO && (
              <button
                className="menu-item"
                onClick={() => {
                  setMenu(false);
                  set({ panel: "settings", panelArg: { tab: "account" } });
                }}
              >
                <span className="menu-ico">
                  <CreditCard size={14} />
                </span>
                <span className="menu-label">My subscription</span>
              </button>
            )}
            <button
              className="menu-item"
              onClick={() => {
                setMenu(false);
                set({ panel: "shortcuts" });
              }}
            >
              <span className="menu-ico">
                <Keyboard size={14} />
              </span>
              <span className="menu-label">Keyboard shortcuts</span>
              <span className="menu-kbd">?</span>
            </button>
            <div className="menu-sep" />
            {/* the demo has no account to leave: its way out is a board of your own (src/demo) */}
            {DEMO ? (
              <a className="menu-item" href={START_URL}>
                <span className="menu-ico">
                  <Plus size={14} />
                </span>
                <span className="menu-label">Start your own board</span>
              </a>
            ) : (
              <button
                className="menu-item"
                onClick={() => {
                  if (!ws?.email && !confirm("This board is only saved on this device. Signing out without an email means you cannot get back to it. Sign out anyway?")) return;
                  void signOut();
                }}
              >
                <span className="menu-ico">
                  <LogOut size={14} />
                </span>
                <span className="menu-label">Sign out</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
